import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { axisProps, chartColors, formatMonth } from '@shared/lib/chart-theme';
import { ChartTooltip } from '@features/analytics/ui/ChartTooltip';
import type { SubscriptionFlow } from '@features/analytics/model/dashboard.types';

/**
 * Subscriptions started against subscriptions lost — the shape investors read
 * for durability, not just growth.
 *
 * Two series, so identity is carried by colour *and* a legend. The pair
 * (blue, orange) is the validated adjacent pair: CVD ΔE 24.7, both above 3:1
 * contrast on white.
 */
export function FlowChart({ data }: { data: SubscriptionFlow[] }) {
  const points = data.map((point) => ({
    month: formatMonth(point.month),
    New: point.created,
    Cancelled: point.cancelled,
  }));

  return (
    <ResponsiveContainer width="100%" height={172}>
      <BarChart data={points} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
        <CartesianGrid stroke={chartColors.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="month" {...axisProps} />
        <YAxis {...axisProps} width={32} allowDecimals={false} />

        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(11,11,11,0.04)' }} />
        <Legend
          verticalAlign="top"
          align="right"
          height={28}
          iconType="circle"
          iconSize={8}
          formatter={(value: string) => (
            <span className="text-xs text-slate-500">{value}</span>
          )}
        />

        {/* 2px surface gap between adjacent bars keeps the pair legible. */}
        <Bar dataKey="New" fill={chartColors.series[0]} radius={[3, 3, 0, 0]} maxBarSize={14} />
        <Bar
          dataKey="Cancelled"
          fill={chartColors.series[1]}
          radius={[3, 3, 0, 0]}
          maxBarSize={14}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}
